<template>
  <div class="flex flex-col w-full bg-white p-5 mt-6 mb-6 rounded shadow-md">

    <div class="flex items-center mb-4">
      <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mr-4">
        COSHH &ndash; LEV Test Results
      </h3>
      <hr class="flex-grow border-primary border-t-2 mr-4"/>
    </div>

    <div v-if="locked" class="mb-4 flex items-center bg-gray-100 border border-gray-300 rounded px-3 py-2 text-sm text-gray-600">
      <Icon iconType="check" class="mr-2 text-primary"/>
      This certificate has been finalised and is locked for editing.
    </div>
    <div v-else-if="showErrors && validationErrors.length" class="mb-4 bg-red-50 border border-red-300 rounded px-3 py-2 text-sm text-red-800">
      <div class="font-semibold mb-1">Fix the boxes marked in red before completing:</div>
      <ul class="list-disc pl-5">
        <li v-for="error in validationErrors" :key="error.message">{{ error.message }}</li>
      </ul>
    </div>
    <div v-else-if="!isValid()" class="mb-4 flex items-start bg-yellow-50 border border-yellow-300 rounded px-3 py-2 text-sm text-yellow-800">
      Before this certificate can be marked complete: fill in at least one column in Test 1 and one column in Test 2 (each used column needs a numeric diameter and velocity), and complete the Fan Details below (use "N/A" for anything that isn't available).
    </div>

    <fieldset :disabled="locked" class="flex flex-col" :class="{ 'opacity-60': locked }">

      <!-- TEST 1 - ALL POINTS OPEN -->
      <div class="mb-6">
        <div class="flex items-center mb-2">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mr-4">Test 1 &ndash; All Points Open</h3>
          <hr class="flex-grow border-primary border-t-2 mr-4"/>
          <span class="text-xs text-gray-500 whitespace-nowrap">at least one column required <span class="text-red-500">*</span></span>
        </div>
        <div class="overflow-x-auto bg-gray-100 p-4 rounded-md">
          <table class="w-full text-sm border-collapse">
            <thead>
            <tr class="bg-gray-200 uppercase font-semibold text-center">
              <th class="p-2 text-left w-1/8">Extraction Point</th>
              <th v-for="n in 10" :key="n" class="p-2 w-1/16">{{ n }}</th>
            </tr>
            </thead>
            <tbody>
            <tr class="hover:bg-gray-200 transition-all duration-150">
              <td class="p-2 pl-2 font-medium">Test Diameter (mm)</td>
              <td v-for="(point, idx) in form.test1.points" :key="'d1-' + idx" class="p-1">
                <input type="text" v-model="point.diameter" class="input-style" :class="{ 'input-error': hasError('test1-d-' + idx) }" :disabled="locked"/>
              </td>
            </tr>
            <tr class="hover:bg-gray-200 transition-all duration-150">
              <td class="p-2 pl-2 font-medium">Recorded Velocity (m/sec)</td>
              <td v-for="(point, idx) in form.test1.points" :key="'v1-' + idx" class="p-1">
                <input type="text" v-model="point.velocity" class="input-style" :class="{ 'input-error': hasError('test1-v-' + idx) }" :disabled="locked"/>
              </td>
            </tr>
            <tr>
              <td class="p-2 pl-2 font-medium">Calculated Volume (m&sup3;/min)</td>
              <td v-for="(point, idx) in form.test1.points" :key="'c1-' + idx" class="p-1">
                <input type="text" :value="calculatedVolume(point)" class="input-style text-gray-500" readonly disabled/>
              </td>
            </tr>
            </tbody>
          </table>

          <div class="flex justify-end items-center mt-3 pr-2">
            <label class="uppercase font-semibold text-sm mr-3">Total System Volume (m&sup3;/min)</label>
            <input type="text" :value="totalSystemVolume" class="input-style text-gray-500 w-32" readonly disabled/>
          </div>
        </div>
        <div class="mt-2">
          <label class="uppercase font-semibold text-sm">Notes</label>
          <textarea v-model="form.test1.notes" rows="2" class="w-full bge-input bge-input-spacing rounded" :disabled="locked"/>
        </div>
      </div>

      <!-- TEST 2 - ALL POINTS CLOSED -->
      <div class="mb-6">
        <div class="flex items-center mb-2">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mr-4">Test 2 &ndash; All Points Closed</h3>
          <hr class="flex-grow border-primary border-t-2 mr-4"/>
          <span class="text-xs text-gray-500 whitespace-nowrap">at least one column required <span class="text-red-500">*</span></span>
        </div>
        <div class="overflow-x-auto bg-gray-100 p-4 rounded-md">
          <table class="w-full text-sm border-collapse">
            <thead>
            <tr class="bg-gray-200 uppercase font-semibold text-center">
              <th class="p-2 text-left w-1/8">Extraction Point</th>
              <th v-for="n in 10" :key="n" class="p-2 w-1/16">{{ n }}</th>
            </tr>
            </thead>
            <tbody>
            <tr class="hover:bg-gray-200 transition-all duration-150">
              <td class="p-2 pl-2 font-medium">Test Diameter (mm)</td>
              <td v-for="(point, idx) in form.test2.points" :key="'d2-' + idx" class="p-1">
                <input type="text" v-model="point.diameter" class="input-style" :class="{ 'input-error': hasError('test2-d-' + idx) }" :disabled="locked"/>
              </td>
            </tr>
            <tr class="hover:bg-gray-200 transition-all duration-150">
              <td class="p-2 pl-2 font-medium">Recorded Velocity (m/sec)</td>
              <td v-for="(point, idx) in form.test2.points" :key="'v2-' + idx" class="p-1">
                <input type="text" v-model="point.velocity" class="input-style" :class="{ 'input-error': hasError('test2-v-' + idx) }" :disabled="locked"/>
              </td>
            </tr>
            <tr>
              <td class="p-2 pl-2 font-medium">Calculated Volume (m&sup3;/min)</td>
              <td v-for="(point, idx) in form.test2.points" :key="'c2-' + idx" class="p-1">
                <input type="text" :value="calculatedVolume(point)" class="input-style text-gray-500" readonly disabled/>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-2">
          <label class="uppercase font-semibold text-sm">Notes</label>
          <textarea v-model="form.test2.notes" rows="2" class="w-full bge-input bge-input-spacing rounded" :disabled="locked"/>
        </div>
      </div>

      <!-- VISUAL CHECK -->
      <div class="mb-6">
        <div class="flex items-center mb-2">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mr-4">Visual Check</h3>
          <hr class="flex-grow border-primary border-t-2"/>
        </div>
        <div class="bg-gray-100 p-4 rounded-md">
          <div class="grid grid-cols-12 rounded-md text-left font-medium bg-gray-200 p-2 uppercase font-semibold">
            <div class="col-span-5">Item</div>
            <div class="col-span-3 text-center">Result</div>
            <div class="col-span-4">Comments</div>
          </div>

          <div v-for="(item, index) in form.visual_checks" :key="index" class="mt-1">
            <div class="grid grid-cols-12 items-center py-2 hover:bg-gray-200 transition-all duration-150">
              <div class="col-span-5 pl-2">{{ item.label }}</div>
              <div class="col-span-3 flex justify-center items-center space-x-3">
                <button type="button" class="p-1 rounded border" :disabled="locked"
                        :class="item.result === 'tick' ? 'bg-green-100 border-green-500' : 'border-gray-300'"
                        @click="item.result = (item.result === 'tick' ? '' : 'tick')">
                  <Icon iconType="check" :class="item.result === 'tick' ? 'text-green-600' : 'text-gray-400'"/>
                </button>
                <button type="button" class="p-1 rounded border" :disabled="locked"
                        :class="item.result === 'cross' ? 'bg-red-100 border-red-500' : 'border-gray-300'"
                        @click="item.result = (item.result === 'cross' ? '' : 'cross')">
                  <Icon iconType="close" :class="item.result === 'cross' ? 'text-red-600' : 'text-gray-400'"/>
                </button>
              </div>
              <div class="col-span-4 pr-2">
                <input type="text" v-model="item.comment" placeholder="Optional" class="w-full bge-input bge-input-spacing rounded" :disabled="locked"/>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col md:flex-row mt-3">
          <div class="w-full md:w-1/2 p-1">
            <label class="uppercase font-semibold text-sm">System Actual Performance</label>
            <input type="number" step="any" v-model="form.system_actual_performance" class="w-full bge-input bge-input-spacing rounded" :disabled="locked"/>
          </div>
          <div class="w-full md:w-1/2 p-1">
            <label class="uppercase font-semibold text-sm">System Design Performance</label>
            <input type="number" step="any" v-model="form.system_design_performance" class="w-full bge-input bge-input-spacing rounded" :disabled="locked"/>
          </div>
        </div>
      </div>

      <!-- FAN DETAILS -->
      <div class="mb-6">
        <div class="flex items-center mb-2">
          <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mr-4">Fan Details</h3>
          <hr class="flex-grow border-primary border-t-2 mr-4"/>
          <span class="text-xs text-gray-500 whitespace-nowrap">all fields required <span class="text-red-500">*</span> &ndash; enter "N/A" if not applicable</span>
        </div>
        <div class="flex flex-col md:flex-row bg-gray-50 rounded-md p-3">
          <div class="w-full md:w-1/3 p-1">
            <label class="uppercase font-semibold text-sm">Manufacturer <span class="text-red-500">*</span></label>
            <input type="text" v-model="form.fan_details.manufacturer" placeholder="N/A" class="w-full bge-input bge-input-spacing rounded" :class="{ 'input-error': hasError('fan-manufacturer') }" :disabled="locked"/>
          </div>
          <div class="w-full md:w-1/3 p-1">
            <label class="uppercase font-semibold text-sm">Model <span class="text-red-500">*</span></label>
            <input type="text" v-model="form.fan_details.model" placeholder="N/A" class="w-full bge-input bge-input-spacing rounded" :class="{ 'input-error': hasError('fan-model') }" :disabled="locked"/>
          </div>
          <div class="w-full md:w-1/3 p-1">
            <label class="uppercase font-semibold text-sm">Motor <span class="text-red-500">*</span></label>
            <input type="text" v-model="form.fan_details.motor" placeholder="N/A" class="w-full bge-input bge-input-spacing rounded" :class="{ 'input-error': hasError('fan-motor') }" :disabled="locked"/>
          </div>
        </div>
        <div class="flex flex-col md:flex-row bg-gray-50 rounded-md p-3 mt-2">
          <div class="w-full md:w-1/3 p-1">
            <label class="uppercase font-semibold text-sm">Phase <span class="text-red-500">*</span></label>
            <input type="text" v-model="form.fan_details.phase" placeholder="N/A" class="w-full bge-input bge-input-spacing rounded" :class="{ 'input-error': hasError('fan-phase') }" :disabled="locked"/>
          </div>
          <div class="w-full md:w-1/3 p-1">
            <label class="uppercase font-semibold text-sm">RPM <span class="text-red-500">*</span></label>
            <input type="text" v-model="form.fan_details.rpm" placeholder="N/A" class="w-full bge-input bge-input-spacing rounded" :class="{ 'input-error': hasError('fan-rpm') }" :disabled="locked"/>
          </div>
          <div class="w-full md:w-1/3 p-1">
            <label class="uppercase font-semibold text-sm">Volume <span class="text-red-500">*</span></label>
            <input type="text" v-model="form.fan_details.volume" placeholder="N/A" class="w-full bge-input bge-input-spacing rounded" :class="{ 'input-error': hasError('fan-volume') }" :disabled="locked"/>
          </div>
        </div>
      </div>

    </fieldset>

    <!-- SYSTEM LAYOUT -->
    <div>
      <div class="flex items-center mb-2">
        <h3 class="uppercase text-lg font-semibold text-primary tracking-wide mr-4">System Layout</h3>
        <hr class="flex-grow border-primary border-t-2"/>
      </div>
      <p class="text-xs text-gray-500 mb-2">
        Sketch the system layout below. It is saved against this certificate and can be redrawn as many times as needed until the certificate is marked complete.
      </p>

      <div v-if="!certificate_id" class="bg-gray-100 border border-gray-300 rounded px-3 py-4 text-sm text-gray-500 text-center">
        Save this certificate to enable the System Layout sketch.
      </div>
      <div v-else class="system-layout-canvas border border-gray-300 rounded">
        <Signature
            :uploadRefId="certificate_id"
            :uploadSignatureF="uploadSystemLayout"
            :getSignatureF="getSystemLayout"
            :deleteSignatureF="deleteSystemLayout"
            :locked="locked"
            :allowRedraw="true"
            :showSketchTools="true"
            documentCategory="system_layout"
            documentRole="system_layout"
        />
      </div>
    </div>

  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import Signature from '@/components/Signature.vue';

const VISUAL_CHECK_LABELS = [
  'Extraction Unit / Fan',
  'Filters',
  'Ductwork',
  'Hoses and Nozzles',
  'Hose Reels',
  'Fume and Swing Arms',
  'Modules',
  'Other'
];

function blankPoints() {
  return Array.from({ length: 10 }, function () {
    return { diameter: '', velocity: '' };
  });
}

function blankForm() {
  return {
    test1: { points: blankPoints(), notes: '' },
    test2: { points: blankPoints(), notes: '' },
    visual_checks: VISUAL_CHECK_LABELS.map(function (label) {
      return { label: label, result: '', comment: '' };
    }),
    system_actual_performance: '',
    system_design_performance: '',
    fan_details: { manufacturer: '', model: '', motor: '', phase: '', rpm: '', volume: '' }
  };
}

export default {
  name: 'CoshhLev',
  components: { Icon, Signature },
  props: {
    calibration_details: {
      type: Object,
      default: function () { return {}; }
    },
    certificate_id: {
      type: String,
      default: ''
    },
    locked: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:calibration_details'],
  computed: {
    // Every problem blocking completion, with the box it belongs to. It is
    // worked out live, so a box stops being red as soon as it is fixed.
    validationErrors: function () {
      const self = this;
      const errors = [];

      ['test1', 'test2'].forEach(function (testKey) {
        const label = testKey === 'test1' ? 'Test 1' : 'Test 2';
        const points = (self.form[testKey] && self.form[testKey].points) || [];
        let hasComplete = false;

        points.forEach(function (point, idx) {
          const state = self.pointState(point);
          if (state === 'complete') hasComplete = true;
          if (state !== 'invalid') return;

          const diameter = String(point.diameter != null ? point.diameter : '').trim();
          const velocity = String(point.velocity != null ? point.velocity : '').trim();
          if (!(diameter !== '' && isFinite(parseFloat(diameter)) && parseFloat(diameter) > 0)) {
            errors.push({ boxes: [testKey + '-d-' + idx], message: label + ', point ' + (idx + 1) + ': Test Diameter needs a number above 0' });
          }
          if (!(velocity !== '' && isFinite(parseFloat(velocity)))) {
            errors.push({ boxes: [testKey + '-v-' + idx], message: label + ', point ' + (idx + 1) + ': Recorded Velocity needs a number' });
          }
        });

        const hasInvalid = points.some(function (point) { return self.pointState(point) === 'invalid'; });
        if (!hasComplete && !hasInvalid) {
          errors.push({ boxes: [testKey + '-d-0', testKey + '-v-0'], message: label + ': fill in at least one column (diameter and velocity)' });
        }
      });

      const fanLabels = { manufacturer: 'Manufacturer', model: 'Model', motor: 'Motor', phase: 'Phase', rpm: 'RPM', volume: 'Volume' };
      Object.keys(fanLabels).forEach(function (key) {
        const value = self.form.fan_details[key];
        if (value === null || value === undefined || String(value).trim() === '') {
          errors.push({ boxes: ['fan-' + key], message: 'Fan Details: ' + fanLabels[key] + ' is empty (enter N/A if not available)' });
        }
      });

      return errors;
    },

    totalSystemVolume: function () {
      const self = this;
      const total = this.form.test1.points.reduce(function (sum, point) {
        const volume = self.pointVolume(point);
        return volume === null ? sum : sum + volume;
      }, 0);
      return total.toFixed(2);
    }
  },
  data: function () {
    return {
      form: blankForm(),
      hasUserEdited: false,
      // Red highlights only show once Save & Complete has been tried
      showErrors: false
    };
  },
  mounted: function () {
    this.seedFrom(this.calibration_details);
  },
  watch: {
    calibration_details: {
      deep: true,
      handler: function (val) {
        if (!val) return;
        if (this.hasUserEdited) return;
        this.seedFrom(val);
      }
    },
    form: {
      deep: true,
      handler: function (v) {
        this.hasUserEdited = true;
        this.$emit('update:calibration_details', JSON.parse(JSON.stringify(v)));
      }
    }
  },
  methods: {
    // Volumetric flow in m3/min: velocity (m/s) x duct cross-sectional area
    // from the entered diameter (mm) x 60. Blank until the point has both a
    // numeric diameter and velocity.
    pointVolume: function (point) {
      const diameter = parseFloat(point && point.diameter);
      const velocity = parseFloat(point && point.velocity);
      if (!isFinite(diameter) || !isFinite(velocity) || diameter <= 0) return null;
      const area = Math.PI * Math.pow(diameter / 1000, 2) / 4;
      return velocity * area * 60;
    },

    calculatedVolume: function (point) {
      const volume = this.pointVolume(point);
      return volume === null ? '' : volume.toFixed(2);
    },

    pointState: function (point) {
      const diameter = String((point && point.diameter) != null ? point.diameter : '').trim();
      const velocity = String((point && point.velocity) != null ? point.velocity : '').trim();
      if (diameter === '' && velocity === '') return 'empty';
      const diameterOk = diameter !== '' && isFinite(parseFloat(diameter)) && parseFloat(diameter) > 0;
      const velocityOk = velocity !== '' && isFinite(parseFloat(velocity));
      return (diameterOk && velocityOk) ? 'complete' : 'invalid';
    },

    testIsValid: function (test) {
      const self = this;
      const states = (test && Array.isArray(test.points) ? test.points : []).map(function (p) { return self.pointState(p); });
      const hasComplete = states.indexOf('complete') !== -1;
      const hasInvalid = states.indexOf('invalid') !== -1;
      return hasComplete && !hasInvalid;
    },

    seedFrom: function (src) {
      const safe = (src && typeof src === 'object') ? src : {};
      const blank = blankForm();

      const seedTest = function (t, fallback) {
        const safeT = (t && typeof t === 'object') ? t : {};
        const points = Array.isArray(safeT.points) ? safeT.points : fallback.points;
        return {
          points: fallback.points.map(function (_, idx) {
            const p = points[idx] || {};
            return {
              diameter: (p.diameter != null) ? p.diameter : '',
              velocity: (p.velocity != null) ? p.velocity : ''
            };
          }),
          notes: (safeT.notes != null) ? safeT.notes : ''
        };
      };

      const visualChecks = Array.isArray(safe.visual_checks) ? safe.visual_checks : [];

      this.form = {
        test1: seedTest(safe.test1, blank.test1),
        test2: seedTest(safe.test2, blank.test2),
        visual_checks: VISUAL_CHECK_LABELS.map(function (label) {
          const existing = visualChecks.find(function (v) { return v && v.label === label; }) || {};
          return {
            label: label,
            result: existing.result || '',
            comment: existing.comment || ''
          };
        }),
        system_actual_performance: (safe.system_actual_performance != null) ? safe.system_actual_performance : '',
        system_design_performance: (safe.system_design_performance != null) ? safe.system_design_performance : '',
        fan_details: {
          manufacturer: (safe.fan_details && safe.fan_details.manufacturer) || '',
          model: (safe.fan_details && safe.fan_details.model) || '',
          motor: (safe.fan_details && safe.fan_details.motor) || '',
          phase: (safe.fan_details && safe.fan_details.phase) || '',
          rpm: (safe.fan_details && safe.fan_details.rpm) || '',
          volume: (safe.fan_details && safe.fan_details.volume) || ''
        }
      };

      this.hasUserEdited = false;
    },

    hasError: function (key) {
      return this.showErrors && this.validationErrors.some(function (e) { return e.boxes.indexOf(key) !== -1; });
    },

    // Called by the certificate page when Save & Complete is pressed: turns on
    // the red highlights and returns the messages to show.
    showValidation: function () {
      this.showErrors = true;
      return this.validationErrors.map(function (e) { return e.message; });
    },

    getCurrentData: function () {
      return JSON.parse(JSON.stringify(this.form));
    },

    isValid: function () {
      const test1Ok = this.testIsValid(this.form.test1);
      const test2Ok = this.testIsValid(this.form.test2);
      const fan = this.form.fan_details;
      const fanFilled = ['manufacturer', 'model', 'motor', 'phase', 'rpm', 'volume']
          .every(function (key) { return fan[key] !== null && fan[key] !== undefined && String(fan[key]).trim() !== ''; });

      return test1Ok && test2Ok && fanFilled;
    },

    async uploadSystemLayout(files, refId) {
      const result = await this.CertificateService.createCertificateDocument(refId, files[0], true);
      return [result];
    },

    async getSystemLayout(refId, documentRole) {
      const docs = await this.CertificateService.getCertificateDocuments(refId, documentRole);
      return Array.isArray(docs) ? docs : [];
    },

    async deleteSystemLayout(refId, documentId) {
      await this.CertificateService.deleteCertificateDocument(refId, documentId);
    }
  }
};
</script>

<style scoped>
.input-error {
  border: 1px solid #ef4444 !important;
  background-color: #fef2f2 !important;
}

.input-style {
  padding: 0.5rem;
  background: white;
  border: 1px solid #eee;
  border-radius: 0.25rem;
  width: 100%;
  text-align: center;
}

.system-layout-canvas ::v-deep .signature-canvas {
  height: 700px !important;
  max-width: 100% !important;
}

.system-layout-canvas ::v-deep .signature-img {
  max-width: 100% !important;
}
</style>
